import styled, { css } from 'styled-components';
import { shade } from 'polished';

interface OverlayProps {
  isOpen: boolean;
}

interface SidebarProps {
  isOpen: boolean;
}

interface MessageBubbleProps {
  from: 'user' | 'agent';
}

export const Overlay = styled.div<OverlayProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
`;

export const Sidebar = styled.aside<SidebarProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100vw;
  background: #fff;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  transform: translateX(${props => (props.isOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
`;

export const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: #fff;
  border-bottom: 1px solid ${props => props.theme['gray-100']};

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme['gray-500']};
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: ${props => props.theme['gray-300']};
    transition: color 0.2s;

    &:hover {
      color: ${props => props.theme['red-500']};
    }

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

export const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${props => props.theme['gray-300']};
  padding: 0 16px;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }

  p {
    font-size: 15px;
    line-height: 22px;
    max-width: 280px;
  }
`;

export const MessageBubble = styled.div<MessageBubbleProps>`
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 20px;
  word-break: break-word;
  white-space: pre-wrap;

  ${props =>
    props.from === 'user'
      ? css`
          align-self: flex-end;
          background: ${props.theme['red-500']};
          color: #fff;
          border-bottom-right-radius: 4px;
        `
      : css`
          align-self: flex-start;
          background: ${props.theme['gray-100']};
          color: ${props.theme['gray-500']};
          border-bottom-left-radius: 4px;
        `}
`;

export const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-self: flex-start;
  max-width: 85%;
`;

export const Chip = styled.button`
  background: #fff;
  border: 1px solid ${props => props.theme['gray-300']};
  color: ${props => props.theme['gray-500']};
  border-radius: ${props => props.theme['--border-radius']};
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme['red-500']};
    border-color: ${props => props.theme['red-500']};
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ResultCard = styled.div`
  align-self: stretch;
  background: ${props => props.theme['gray-100']};
  border-left: 3px solid ${props => props.theme['red-500']};
  border-radius: 8px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .ncm {
    font-size: 22px;
    font-weight: 700;
    color: ${props => props.theme['gray-500']};
  }

  .row {
    display: flex;
    flex-direction: column;
    gap: 2px;

    span {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: ${props => props.theme['gray-300']};
    }

    strong,
    p {
      font-size: 14px;
      color: ${props => props.theme['gray-500']};
      font-weight: 500;
    }

    p {
      font-weight: 400;
      line-height: 20px;
    }
  }
`;

export const CopyButton = styled.button`
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${props => props.theme['red-500']};
  color: #fff;
  border: 0;
  border-radius: ${props => props.theme['--border-radius']};
  height: 44px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${shade(0.2, '#ff0000')};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const InputArea = styled.form`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid ${props => props.theme['gray-100']};

  input {
    flex: 1;
    height: 48px;
    padding: 0 16px;
    border: 2px solid transparent;
    border-radius: ${props => props.theme['--border-radius']};
    background: ${props => props.theme['gray-100']};
    color: ${props => props.theme['gray-500']};
    font-size: 14px;
    transition: border-color 0.2s;

    &::placeholder {
      color: ${props => props.theme['gray-300']};
    }

    &:focus {
      outline: 0;
      border-color: ${props => props.theme['red-500']};
    }

    &:disabled {
      opacity: 0.6;
    }
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    background: ${props => props.theme['red-500']};
    color: #fff;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
      background: ${shade(0.2, '#ff0000')};
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const Loading = styled.div`
  align-self: flex-start;
  font-size: 13px;
  color: ${props => props.theme['gray-300']};
  font-style: italic;
`;
